import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { Loader2, Send, Sparkles } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Badge } from "../components/ui/Badge";
import { cn } from "../lib/cn";

interface Msg {
  role: "user" | "assistant";
  text: string;
  comparison?: { name: string; sf: boolean; sap: boolean }[];
}

const SUGGESTIONS = [
  "Which accounts exist in Salesforce but not in SAP?",
  "Show open opportunities over $50k by owner",
  "Compare customer emails across CRM and billing",
];

const DEMO_COMPARISON = [
  { name: "Acme Corp", sf: true, sap: false },
  { name: "Globex", sf: true, sap: true },
  { name: "Initech", sf: true, sap: false },
  { name: "Umbrella", sf: true, sap: true },
];

export default function Copilot() {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  function ask(text: string) {
    if (!text.trim() || thinking) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setThinking(true);
    timer.current = window.setTimeout(() => {
      const showTable = /salesforce|sap|account|crm/i.test(text);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: showTable
            ? "I queried Salesforce and SAP in parallel and reconciled the results. Accounts present in Salesforce but missing from SAP:"
            : "In the live product I'd fan this out across your connected systems and reconcile the results with citations. Connect a system to run it for real.",
          comparison: showTable ? DEMO_COMPARISON : undefined,
        },
      ]);
      setThinking(false);
    }, 1300);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    ask(input);
  }

  return (
    <>
      <PageHeader
        title="Copilot"
        description="Ask a question in natural language and get an answer reconciled across every connected system."
        actions={<Badge tone="violet">Preview</Badge>}
      />

      <div className="mx-auto flex h-[62vh] max-w-2xl flex-col rounded-2xl border border-slate-200 bg-white shadow-card">
        <div className="flex-1 space-y-5 overflow-y-auto p-6">
          {messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-lift">
                <Sparkles className="h-7 w-7" />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">Ask across your systems</h3>
              <p className="mt-1 max-w-sm text-sm text-slate-400">
                Federated queries decompose your question, run it in parallel, and cite every source.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => ask(s)}
                    className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs text-slate-500 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div key={i} className={cn("flex gap-3", m.role === "user" && "flex-row-reverse")}>
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                    m.role === "user" ? "bg-slate-800 text-white" : "bg-brand-gradient text-white",
                  )}
                >
                  {m.role === "user" ? "You" : <Sparkles className="h-4 w-4" />}
                </span>
                <div
                  className={cn(
                    "max-w-[80%] rounded-2xl px-4 py-2.5 text-sm",
                    m.role === "user"
                      ? "rounded-tr-sm bg-slate-800 text-white"
                      : "rounded-tl-sm border border-slate-200 bg-white text-slate-700",
                  )}
                >
                  <p>{m.text}</p>
                  {m.comparison && (
                    <div className="mt-3 overflow-hidden rounded-xl border border-slate-100">
                      {m.comparison.map((r) => (
                        <div key={r.name} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-xs last:border-0">
                          <span className="font-medium text-slate-700">{r.name}</span>
                          <span className="flex items-center gap-3">
                            <span className="text-brand-600">SF ✓</span>
                            <span className={r.sap ? "text-slate-400" : "font-semibold text-rose-600"}>
                              {r.sap ? "SAP ✓" : "SAP ✗"}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {thinking && (
            <div className="flex gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-gradient text-white">
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="flex items-center gap-2 rounded-2xl rounded-tl-sm border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Querying systems…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="border-t border-slate-100 p-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white pl-4 focus-within:border-accent-400">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything across your systems…"
              className="flex-1 bg-transparent py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
            <button
              type="submit"
              disabled={!input.trim() || thinking}
              className="m-1 flex h-9 w-9 items-center justify-center rounded-lg bg-brand-gradient text-white shadow-lift disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
